import 'keen-slider/keen-slider.min.css'

import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'
import {useKeenSlider} from 'keen-slider/react'

import {CurrentPaneIndicators} from '@vio/atlas/components/designSystem/helpers/SwipePanes/CurrentPaneIndicators'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {AssetPaths, loadBrandAssets} from './loadBrandAssets'

const CURRENT_INDICATORS_WIDTH = 50
const ILLUSTRATION_DESKTOP_HEIGHT = 135
const ILLUSTRATION_MOBILE_HEIGHT = 110

const Container = styled.div`
  margin: 0 ${cssTheme.layout.spacing.s300};
  padding: 0;

  ${linariaMq.desktopXs} {
    background: ${cssTheme.colors.background.brand.tertiary};
    border-radius: ${cssTheme.layout.radius.xxl};
    margin: 0;
    padding: ${cssTheme.layout.spacing.s800} ${cssTheme.layout.spacing.s950};
  }
`

const Title = styled(Text)`
  margin: 0 0 ${cssTheme.layout.spacing.s300} 0;
  text-align: left;
  ${linariaMq.desktopXs} {
    text-align: center;
    margin: 0 0 ${cssTheme.layout.spacing.s600} 0;
  }
`

const StepsContainer = styled.div`
  ${linariaMq.desktopXs} {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;
  }
`

const Step = styled.div`
  background: ${cssTheme.colors.background.brand.tertiary};
  border-radius: ${cssTheme.layout.radius.xxl};
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 272px;
  position: relative;
  text-align: left;
  padding: ${cssTheme.layout.spacing.s600};

  ${linariaMq.desktopXs} {
    margin-right: 0;
    background: none;
    border-radius: 0;
    padding: 0;
  }
`

const StepIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${linariaMq.desktopXs} {
    margin-bottom: ${cssTheme.layout.spacing.s500};
  }
`

const StepIcon = styled.img`
  height: ${ILLUSTRATION_MOBILE_HEIGHT}px;
  width: auto;
  ${linariaMq.desktopXs} {
    height: ${ILLUSTRATION_DESKTOP_HEIGHT}px;
  }
`

const StepContent = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s200};
`

const StepNumber = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: ${cssTheme.colors.content.brand.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  ${cssTheme.typography.titleM};
  border: 1px solid ${cssTheme.colors.border.brand.primary};
  flex-shrink: 0;
  margin-bottom: ${cssTheme.layout.spacing.s200};
`

const StepTextContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 0;
`

const ConnectingLine = styled.img`
  display: block;
  position: relative;
  top: 70px;
`

const CurrentPaneIndicatorsWrapper = styled.div`
  position: relative;
  padding-top: ${cssTheme.layout.spacing.s800};
  margin: 0 auto;
  width: ${CURRENT_INDICATORS_WIDTH / 0.75}px;
`

const HighlightCashback = styled.span`
  color: ${cssTheme.colors.content.brand.primary};

  ${linariaMq.desktopXs} {
    color: inherit;
  }
`

export const CashbackExplanationWidget: React.FC<{isRtl?: boolean}> = ({
  isRtl = false
}) => {
  const {formatMessage} = useIntl()
  const {brandCode} = useBrand()
  const {
    monetisation: {cashback}
  } = useBrandCapabilities()
  const {isMobile} = useDeviceLayout()
  const toggle = useToggle()
  const [currentSlide, setCurrentSlide] = useState(0)

  const [sliderRef] = useKeenSlider<HTMLDivElement>({
    loop: false,
    mode: 'snap',
    initial: 0,
    disabled: !isMobile,
    rtl: isRtl,
    slides: {
      perView: 1.1,
      spacing: 16
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel)
    }
  })

  // State for brand-specific assets (starts with defaults)
  const [assets, setAssets] = useState<AssetPaths | null>(null)

  // Load brand-specific assets asynchronously
  useEffect(() => {
    if (brandCode) {
      loadBrandAssets(brandCode)
        .then(brandAssets => {
          setAssets(brandAssets)
        })
        .catch(() => {
          // If loading fails, keep default assets
          // Error is silently handled - defaults are already set
        })
    }
  }, [brandCode])

  const showExplanation = toggle(
    '4f8c2e1d-cashback-explanation-homepage',
    false,
    true
  )

  if (!cashback.enabled || !showExplanation || !assets) {
    return null
  }

  const {
    cashbackExplanationTitle,
    cashbackExplanationStep1Title,
    cashbackExplanationStep1Description,
    cashbackExplanationStep2Title,
    cashbackExplanationStep2Description,
    cashbackExplanationStep3Title,
    cashbackExplanationStep3Description
  } = cashback.config.messages

  return (
    <Container>
      <Title variant={isMobile ? 'titleXL' : 'title2XL'}>
        <FormattedMessageWrapper
          {...cashbackExplanationTitle}
          values={{
            span: chunks => <HighlightCashback>{chunks}</HighlightCashback>
          }}
        />
      </Title>
      <StepsContainer ref={sliderRef} className="keen-slider">
        <Step className="keen-slider__slide">
          <StepIconContainer>
            <StepIcon
              src={assets.step1Icon}
              alt="Mobile phone with checkmark"
            />
          </StepIconContainer>
          <StepContent>
            <StepNumber>1</StepNumber>
            <StepTextContent>
              <Text variant="titleM">
                {formatMessage(cashbackExplanationStep1Title!)}
              </Text>
              <Text variant="bodyS">
                {formatMessage(cashbackExplanationStep1Description!)}
              </Text>
            </StepTextContent>
          </StepContent>
        </Step>

        {!isMobile && (
          <ConnectingLine
            src={assets.connectingLine1}
            alt=""
            role="presentation"
            width={128}
          />
        )}

        <Step className="keen-slider__slide">
          <StepIconContainer>
            <StepIcon src={assets.step2Icon} alt="Browser with shopping cart" />
          </StepIconContainer>
          <StepContent>
            <StepNumber>2</StepNumber>
            <StepTextContent>
              <Text variant="titleM">
                {formatMessage(cashbackExplanationStep2Title!)}
              </Text>
              <Text variant="bodyS">
                {formatMessage(cashbackExplanationStep2Description!)}
              </Text>
            </StepTextContent>
          </StepContent>
        </Step>

        {!isMobile && (
          <ConnectingLine
            src={assets.connectingLine2}
            alt=""
            role="presentation"
            width={117}
          />
        )}

        <Step className="keen-slider__slide">
          <StepIconContainer>
            <StepIcon
              src={assets.step3Icon}
              alt="Mobile phone with payment cards"
            />
          </StepIconContainer>
          <StepContent>
            <StepNumber>3</StepNumber>
            <StepTextContent>
              <Text variant="titleM">
                {formatMessage(cashbackExplanationStep3Title!)}
              </Text>
              <Text variant="bodyS">
                {formatMessage(cashbackExplanationStep3Description!)}
              </Text>
            </StepTextContent>
          </StepContent>
        </Step>
      </StepsContainer>
      {isMobile && (
        <CurrentPaneIndicatorsWrapper>
          <CurrentPaneIndicators
            amount={3}
            invertDotColor
            currentIndex={currentSlide}
            width={CURRENT_INDICATORS_WIDTH}
            hasDarkGradient={false}
            isRtl={isRtl}
          />
        </CurrentPaneIndicatorsWrapper>
      )}
    </Container>
  )
}
